import { useEffect, useRef } from 'react';

/**
 * Live-refresh helper: runs `fn` every `intervalMs` while the tab is visible,
 * plus immediately when the tab regains visibility/focus. Pauses in background
 * tabs so idle dashboards don't hammer the API.
 *
 * This is the app-wide "live updates" primitive. True push (WebSockets) is not
 * available on Cloudflare Pages Functions without a separate Durable Objects
 * worker — if that lands one day, swap the internals here and every consumer
 * upgrades for free.
 */
export function useLiveRefresh(fn: () => void, intervalMs: number, enabled: boolean = true) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled) return;
    let interval: number | null = null;

    const start = () => {
      if (interval == null) interval = window.setInterval(() => fnRef.current(), intervalMs);
    };
    const stop = () => {
      if (interval != null) { window.clearInterval(interval); interval = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') { fnRef.current(); start(); }
      else stop();
    };

    fnRef.current(); // immediate first run, then on the interval
    start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, [intervalMs, enabled]);
}
